import { Component, HostBinding, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppState } from '../../app.state';
import { DeliveryDefenseService } from '../services/delivery-defense.service';
import { finalize, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { NotificationType } from '../../shared/models/notification-type';
import dayjs from 'dayjs';

@Component({
    selector: 'upsc-delivery-defense-subscription-renew-dialog',
    templateUrl: './delivery-defense-subscription-renew-dialog.component.html',
    styleUrls: ['./delivery-defense-subscription-renew-dialog.component.scss'],
})
export class DeliveryDefenseSubscriptionRenewDialogComponent implements OnInit {
    @HostBinding('class') public hostClass = 'delivery-defense-subscription-renew-dialog';

    @ViewChild('complimentary', { static: true }) complimentary: TemplateRef<any>;
    @ViewChild('regular', { static: true }) regular: TemplateRef<any>;
    @ViewChild('form', { static: true }) form: TemplateRef<any>;
    @ViewChild('upgrade', { static: true }) upgrade: TemplateRef<any>;
    @ViewChild('downgrade', { static: true }) downgrade: TemplateRef<any>;

    public mode: 'complimentary' | 'regular' | 'form' | 'upgrade' | 'downgrade';

    public formGroup: UntypedFormGroup;
    public isFormSubmitting = false;

    public regularData = {
        remainingCredit: 0,
        fullCredit: 50,
        price: 'Complimentary',
    };

    public complimentaryData = {
        complimentaryCredit: 50,
        price: 'Complimentary',
        trialPeriodDays: 30,
    };

    public upgradeData = {
        subscriptionPeriodDays: 30,
        shipmentCount: 100,
        highVolumeProgramNumber: 1,
    };

    public downgradeData = {
        subscriptionPeriodDays: 30,
        name: null,
        oldHighVolumeProgramNumber: 1,
        newHighVolumeProgramNumber: 2,
        newProgram: 'Value Add Subscription Plan',
        currentMonth: dayjs().format('MMMM'),
    };

    constructor(public dialogRef: MatDialogRef<DeliveryDefenseSubscriptionRenewDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public data: any,
                private readonly formBuilder: UntypedFormBuilder,
                private readonly deliveryDefenseService: DeliveryDefenseService,
                private readonly router: Router,
                private notificationService: NotificationService,
                private readonly appState: AppState,
    ) {
        this.mode = this.data.mode;
        if (this.data?.upgrade) {
            this.upgradeData.shipmentCount = this.data.upgrade.shipmentCount;
            this.upgradeData.highVolumeProgramNumber = this.data.upgrade.highVolumeProgramNumber;
        }

        if (this.data?.downgrade) {
            this.downgradeData.oldHighVolumeProgramNumber = this.data.downgrade.oldHighVolumeProgramNumber;
            this.downgradeData.newHighVolumeProgramNumber = this.data.downgrade.newHighVolumeProgramNumber;
            this.downgradeData.newProgram = this.data.downgrade.newHighVolumeProgramNumber === 0
                ? 'Value Add Program'
                : `High Volume Program ${ this.data.downgrade.newHighVolumeProgramNumber }`;
        }

        this.formGroup = this.formBuilder.group({
            firstName: [null, Validators.required],
            lastName: [null, Validators.required],
            phone: [null, Validators.required],
            email: [null, Validators.required],
        });

        const currentUser = this.appState.user$();
        const pascalFirstName = `${ currentUser?.FirstName?.[0]?.toUpperCase() }${ currentUser?.FirstName?.slice(1) }`;
        const lastInitial = `${ currentUser?.LastName?.[0]?.toUpperCase() }.` || '';
        this.downgradeData.name = `${ pascalFirstName } ${ lastInitial }`;
    }

    public get modeTemplate(): TemplateRef<any> {
        switch (this.mode) {
            case 'complimentary':
                return this.complimentary;
            case 'regular':
                return this.regular;
            case 'form':
                return this.form;
            case 'upgrade':
                return this.upgrade;
            case 'downgrade':
                return this.downgrade;
            default:
                return null;
        }
    }

    public ngOnInit(): void {
        this.fillForm();
    }

    public purchaseSubscription(event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.isFormSubmitting = true;
        this.deliveryDefenseService.subscribeToDeliveryDefense()
            .pipe(
                switchMap(() => {
                    return this.deliveryDefenseService.getRemainingDDCredit();
                }),
                finalize(() => {
                    this.isFormSubmitting = false;
                }),
            )
            .subscribe((ddCredit) => {
                this.deliveryDefenseService.deliveryDefenseCreditsUpdated$.next(ddCredit);

                // this.notificationService.notify(
                //     '<strong>Thanks! Your request is on it\'s way.</strong> A member of our team will contact you soon.',
                //     'Success',
                //     NotificationType.SUCCESS,
                // );

                this.closeSelfDialog();
            });
        this.closeSelfDialog();
    }

    public async requestMoreScores(event: MouseEvent): Promise<void> {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.isFormSubmitting = true;
        this.deliveryDefenseService.requestMoreScore(this.formGroup?.value)
            .pipe(
                finalize(() => {
                    this.isFormSubmitting = false;
                }),
            )
            .subscribe(() => {
                this.notificationService.notify(
                    '<strong>Thanks! Your request is on it\'s way.</strong> A member of our team will contact you soon.',
                    'Success',
                    NotificationType.SUCCESS,
                );

                this.closeSelfDialog();
            });
    }

    public async keepSubscription(event: MouseEvent): Promise<void> {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        await this.router.navigate(['/delivery-defense-sign-up']);
        this.closeSelfDialog();
    }

    public cancelSubscription(event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        this.deliveryDefenseService.showSubscriptionCancellationDialog();
        this.closeSelfDialog();
    }

    public async signUp(event: MouseEvent): Promise<void> {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        await this.router.navigate(['/delivery-defense-sign-up']);
        this.closeSelfDialog();
    }

    private fillForm(): void {
        const currentCustomer = this.appState.customer$();
        this.formGroup?.patchValue({
            firstName: currentCustomer?.CorpContact?.FirstName,
            lastName: currentCustomer?.CorpContact?.LastName,
            phone: currentCustomer?.CorpContact?.TelephoneNo,
            email: currentCustomer?.CorpContact?.Email,
        });
    }

    private closeSelfDialog(): void {
        setTimeout(() => {
            this.dialogRef.close();
        });
    }
}
